var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __decorateParam = (index, decorator) => (target, key) => decorator(target, key, index);

// db/export-data-view.ts
import { Int } from "@adornis/baseql/baseqlTypes.js";
import { Arg, Entity, Subscription } from "@adornis/baseql/decorators.js";
import { AdornisEntity } from "@adornis/baseql/entities/adornisEntity.js";
import { baseqlMetaData } from "@adornis/baseql/metadata/metaDataStore.js";
import { operationName } from "@adornis/baseql/symbols.js";
import { translateFieldName } from "@adornis/field-management/translate-field.js";
import { subscribePaginatedAndFiltered } from "@adornis/filter/db/subscribePaginatedAndFiltered.js";
import { getStandardFilterOperation } from "@adornis/filter/get-standard-filter-operation.js";
import { ConvertConfig, FieldMappingEntry } from "@adornis/import-export/db/convert-config.js";
import { getBestEncodingForUserAgent } from "@adornis/import-export/encoding-helpers.js";
import { exportCSVFromEntities } from "@adornis/import-export/server/export-internals.js";
import { Language } from "@adornis/translation-core/languages.js";
import { Translator } from "@adornis/translation-core/translation.js";
import { DateTime } from "luxon";
import { basename } from "path";
import { Subject, firstValueFrom } from "rxjs";
import { buildFullColumnDefinition } from "../ColumnDefinition.js";
import { convertColumnDefinitionsToSelectionSet } from "../convert-column-definitions.js";
import { View } from "../db/view.js";
import { dataViewTranslations } from "../default-translations.js";
import { getFieldFromEntity } from "../get-field-from-entity.js";
function getOperationName(fullOptions) {
  const operation = fullOptions.operation;
  if (typeof operation === "function")
    return operation[operationName];
  return operation;
}
var getExportFileName = (entityClassName) => `Export_${entityClassName}_${DateTime.now().toFormat("yyyy-LL-dd-HH-mm")}`;
var exportDataView = async (dataViewOptions, exportOptions) => {
  const { XDialog } = await import("@adornis/dialog/x-dialog.js");
  const { downloadLink } = await import("@adornis/file-utils/client/helper-methods.js");
  return downloadLink(
    await XDialog.waitFor(
      (exportOptions?.exportClass ?? ExportDataView).exportDataView(
        dataViewOptions.class._class,
        getOperationName(dataViewOptions),
        dataViewOptions.view,
        exportOptions?.encoding ?? getBestEncodingForUserAgent(),
        exportOptions?.stepSize ?? 100
      )()
    ),
    exportOptions?.fileName ?? getExportFileName(dataViewOptions.class._class)
  );
};
var nodeExtendsEntity = (node, classToExtend) => {
  let iterableNode = node;
  if (iterableNode.type()._class === classToExtend._class)
    return true;
  while (iterableNode = iterableNode.parent) {
    if (iterableNode.type()._class === classToExtend._class)
      return true;
  }
  return false;
};
var ExportDataView = class extends AdornisEntity {
  static translateValue(key, value) {
    if (typeof value === "number")
      value = value.toLocaleString(this._language === "de" ? "de-DE" : "en-EN");
    return value;
  }
  static exportDataView(entityClassName, operationName2, view, encoding = "utf8", stepSize = 100) {
    return () => {
      const node = baseqlMetaData.entities.resolveNode(entityClassName);
      if (!nodeExtendsEntity(node, AdornisEntity))
        throw new Error("Only accepts nodes that inherit AdornisEntity");
      const entityClass = node.type();
      const operation = getStandardFilterOperation(
        entityClass,
        subscribePaginatedAndFiltered,
        operationName2 ?? void 0
      );
      const configuredColumns = view.configuredColumns ?? [];
      const columnDefinitions = configuredColumns.map((fieldConf) => {
        const column = this.basicColumnDefinitions.find(
          (conf) => conf.customID === fieldConf.customID || conf.fieldName === fieldConf.fieldName
        );
        return buildFullColumnDefinition(
          column ?? { fieldName: fieldConf.fieldName ?? void 0, customID: fieldConf.customID ?? void 0 },
          {},
          fieldConf
        );
      });
      const selectionSet = convertColumnDefinitionsToSelectionSet(entityClass, columnDefinitions);
      const subject = new Subject();
      const entityIterable = getEntitiesForExport(
        operation,
        selectionSet,
        entityClass,
        view.filter,
        subject,
        stepSize
      );
      exportCSVFromEntities(
        new ConvertConfig({
          delimiter: ";",
          fieldMappings: columnDefinitions.map((colDef) => {
            let exportFieldName = colDef.fieldName;
            if (!exportFieldName && colDef.customID && colDef.getFieldMapping)
              exportFieldName = colDef.customID;
            if (!exportFieldName)
              return null;
            return new FieldMappingEntry(
              {
                input: translateFieldName(exportFieldName, this._translator)
              },
              () => null,
              (value, data) => {
                let retreivedValue = getFieldFromEntity(data, colDef);
                if (Array.isArray(retreivedValue)) {
                  retreivedValue = retreivedValue.map((v) => this.translateValue(exportFieldName, v));
                } else {
                  retreivedValue = this.translateValue(exportFieldName, retreivedValue);
                }
                return retreivedValue;
              }
            );
          }).filter(Boolean)
        }),
        entityIterable,
        getExportFileName(entityClassName),
        encoding
      ).then((path) => {
        subject.next(`/__export/download/${basename(path)}`);
        subject.complete();
      }).catch((e) => {
        subject.error(e);
      });
      return subject;
    };
  }
};
ExportDataView._class = "ExportDataView";
ExportDataView._language = Language.GERMAN;
ExportDataView._translator = new Translator({
  dictionary: dataViewTranslations,
  languages: [Language.GERMAN, Language.ENGLISH],
  getSelectedLanguage() {
    return ExportDataView._language;
  },
  selectLanguage(lang) {
    ExportDataView._language = lang;
  },
  onDictionaryUpdate() {
  }
});
ExportDataView.basicColumnDefinitions = [];
__decorateClass([
  Subscription((type) => String),
  __decorateParam(0, Arg("entityClassName", (type) => String)),
  __decorateParam(1, Arg("operationName", (type) => String)),
  __decorateParam(2, Arg("view", (type) => View)),
  __decorateParam(3, Arg("encoding", (type) => String)),
  __decorateParam(4, Arg("stepSize", (type) => Int))
], ExportDataView, "exportDataView", 1);
ExportDataView = __decorateClass([
  Entity()
], ExportDataView);
async function* getEntitiesForExport(operation, selectionSet, entityClass, filter, progressSubject, stepSize) {
  let currentStep = 0;
  let lastEntities = await firstValueFrom(operation(currentStep, stepSize, filter)(selectionSet));
  do {
    progressSubject.next(`Collecting data... (${currentStep + lastEntities.length})`);
    for (const entity of lastEntities) {
      yield entity;
    }
    currentStep += stepSize;
  } while ((lastEntities = await firstValueFrom(operation(currentStep, stepSize, filter)(selectionSet))).length !== 0);
}
export {
  ExportDataView,
  exportDataView
};
